import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Minus, Plus, X, ShoppingBag, ArrowRight, Truck } from "lucide-react";
import { useCart } from "@/context/CartContext";

const FREE_SHIP_THRESHOLD = 150;

export default function CartDrawer() {
  const { items, remove, updateQty, subtotal, open, setOpen, count } = useCart();
  const navigate = useNavigate();

  const goToCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="bg-[#0A0A0A] border-l border-white/10 text-white w-full sm:max-w-md p-0 flex flex-col" data-testid="cart-drawer">
        <SheetHeader className="border-b border-white/10 p-6">
          <SheetTitle className="font-display text-2xl text-white flex items-center gap-3">
            <ShoppingBag size={20} /> Your Cart ({count})
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {items.length === 0 ? (
            <div className="text-center text-white/50 py-20" data-testid="empty-cart-msg">
              <p className="font-display text-xl">Your cart is empty.</p>
              <button onClick={() => { setOpen(false); navigate("/shop"); }}
                className="mt-6 btn-outline-gold" data-testid="empty-cart-shop-btn">Browse Shop</button>
            </div>
          ) : (
            items.map((i) => (
              <div key={i.key} className="flex gap-4" data-testid={`cart-item-${i.product_id}`}>
                <img src={i.image_url} alt={i.name} className="w-20 h-24 object-cover" />
                <div className="flex-1">
                  <div className="flex justify-between gap-2">
                    <p className="font-medium text-sm">{i.name}</p>
                    <button onClick={() => remove(i.key)} className="text-white/40 hover:text-[var(--davide-red)]" data-testid={`remove-item-${i.product_id}-btn`}>
                      <X size={16} />
                    </button>
                  </div>
                  {i.size && <p className="text-xs text-white/50 mt-1">Size: {i.size}</p>}
                  <p className="text-[var(--davide-gold)] text-sm mt-2">${(i.price * i.quantity).toFixed(2)}</p>
                  <div className="flex items-center gap-3 mt-3">
                    <button onClick={() => updateQty(i.key, i.quantity - 1)} className="border border-white/20 w-7 h-7 flex items-center justify-center hover:border-[var(--davide-gold)]" data-testid={`decrease-${i.product_id}-btn`}>
                      <Minus size={12} />
                    </button>
                    <span className="text-sm w-6 text-center">{i.quantity}</span>
                    <button onClick={() => updateQty(i.key, i.quantity + 1)} className="border border-white/20 w-7 h-7 flex items-center justify-center hover:border-[var(--davide-gold)]" data-testid={`increase-${i.product_id}-btn`}>
                      <Plus size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-white/10 p-6 space-y-4">
            {/* Free shipping progress */}
            {(() => {
              const remaining = Math.max(0, FREE_SHIP_THRESHOLD - subtotal);
              const pct = Math.min(100, (subtotal / FREE_SHIP_THRESHOLD) * 100);
              return (
                <div data-testid="free-shipping-bar" className="mb-2">
                  <div className="flex items-center gap-2 text-xs text-white/70 mb-2">
                    <Truck size={14} className="text-[var(--davide-gold)]" />
                    {remaining > 0 ? (
                      <span>Add <span className="text-[var(--davide-gold)] font-medium">${remaining.toFixed(2)}</span> more for <span className="text-white">free worldwide shipping</span></span>
                    ) : (
                      <span className="text-[var(--davide-gold)] font-medium">✨ You've unlocked FREE worldwide shipping!</span>
                    )}
                  </div>
                  <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all duration-500"
                      style={{
                        width: `${pct}%`,
                        background: `linear-gradient(90deg, var(--davide-red), var(--davide-gold))`,
                      }}
                    />
                  </div>
                </div>
              );
            })()}

            <div className="flex justify-between items-baseline">
              <span className="overline">Subtotal</span>
              <span className="font-display text-2xl text-[var(--davide-gold)]" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            <p className="text-xs text-white/40">Discount codes & taxes calculated at checkout.</p>
            <button onClick={goToCheckout} className="btn-gold w-full justify-center" data-testid="checkout-btn">
              Checkout <ArrowRight size={16}/>
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
